import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemText,
  ListProps,
  ListSubheader
} from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import type CatalogProduct from '../types/CatalogProduct'
import { useTranslation } from 'react-i18next'
import { formatApperentPower, formatNominalCurrent, formatNominalVoltage } from '../utils/string'
import {
  isAuroraCatalogProduct,
  isElectricalCabinetOrSpineModuleCatalogProduct,
  isGeneratorCatalogProduct,
  isHeaterCatalogProduct,
  isSpineMultiMeasurementCatalogProduct,
  isValveCatalogProduct
} from '../utils/catalogProduct'
import type {
  SpineMultimeasurementCatalogProduct,
  ValveCatalogProduct
} from '../types/CatalogProduct'
import { useCallback, useEffect, useState } from 'react'
import uploadService from '../services/uploadService'
import ImageWithPreview from './ImageWithPreview'
import { CatalogProductLink, useLinkedCatalogProduct } from '../hooks/catalogProduct'
import { CatalogProductType } from '../enums'

export interface CatalogProductDetailsListProps extends Omit<ListProps, 'children'> {
  catalogProduct: CatalogProduct
  showName?: boolean
  showManufacturer?: boolean
  showManufacturerProductCode?: boolean
  showCustomData?: boolean
}

export default function CatalogProductDetailsList({
  catalogProduct,
  showName = true,
  showManufacturer = true,
  showManufacturerProductCode = true,
  showCustomData = true,
  ...props
}: CatalogProductDetailsListProps) {
  const { t } = useTranslation('catalogProduct')
  const [catalogProductPhotoUrl, setCatalogProductPhotoUrl] = useState<string | null>(null)

  const loadCatalogProductPhoto = useCallback(async (photoId: string, signal: AbortSignal) => {
    try {
      const result = await uploadService.getUpload(photoId, { signal })
      setCatalogProductPhotoUrl(result.url)
    } catch {
      if (!signal.aborted) {
        setCatalogProductPhotoUrl(null)
      }
    }
  }, [])

  useEffect(() => {
    const abortController = new AbortController()

    if (!catalogProduct.photo?.id) {
      setCatalogProductPhotoUrl(null)
      return
    }

    loadCatalogProductPhoto(catalogProduct.photo.id, abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [catalogProduct.photo?.id, loadCatalogProductPhoto])

  const formatOutputs = (product: SpineMultimeasurementCatalogProduct) => {
    // Group by nominal current and count.
    const groups: { [key: string]: number } = {}
    for (const output of Object.values(product.customData.outputs)) {
      const key = `${output.nominalCurrent} A`
      groups[key] = (groups[key] || 0) + 1
    }

    // Format the groups into a string.
    return Object.entries(groups)
      .map(([key, count]) => `${count} x ${key}`)
      .join(' + ')
  }

  return (
    <List {...props}>
      {showName && catalogProduct?.name && (
        <CatalogProductNameListItem key="name" catalogProduct={catalogProduct} />
      )}

      {showManufacturer &&
        catalogProduct?.manufacturer &&
        catalogProduct.type !== CatalogProductType.VALVE && (
          <CatalogProductManufacturerListItem key="manufacturer" catalogProduct={catalogProduct} />
        )}

      {showManufacturerProductCode && catalogProduct?.manufacturerProductCode && (
        <CatalogProductManufacturerProductCodeListItem
          key="manufacturerProductCode"
          catalogProduct={catalogProduct}
        />
      )}

      {isGeneratorCatalogProduct(catalogProduct) &&
        showCustomData &&
        catalogProduct?.customData?.apparentPower != null && (
          <ListItemButton key="customData.apparentPower">
            <ListItemText
              primary={
                catalogProduct?.customData?.apparentPower != null
                  ? formatApperentPower(catalogProduct.customData.apparentPower)
                  : '-'
              }
              secondary={t('generator:apparentPower')}
            />
          </ListItemButton>
        )}

      {isGeneratorCatalogProduct(catalogProduct) &&
        showCustomData &&
        catalogProduct?.customData?.nominalVoltage != null && (
          <ListItemButton key="customData.nominalVoltage">
            <ListItemText
              primary={
                catalogProduct?.customData?.nominalVoltage != null
                  ? formatNominalVoltage(catalogProduct.customData.nominalVoltage)
                  : '-'
              }
              secondary={t('generator:nominalVoltage')}
            />
          </ListItemButton>
        )}

      {isGeneratorCatalogProduct(catalogProduct) &&
        showCustomData &&
        catalogProduct?.customData?.nominalCurrent != null && (
          <ListItemButton key="customData.nominalCurrent">
            <ListItemText
              primary={
                catalogProduct?.customData?.nominalCurrent != null
                  ? formatNominalCurrent(catalogProduct.customData.nominalCurrent)
                  : '-'
              }
              secondary={t('generator:nominalCurrent')}
            />
          </ListItemButton>
        )}

      {isGeneratorCatalogProduct(catalogProduct) &&
        showCustomData &&
        catalogProduct?.customData?.powerSource != null && (
          <ListItemButton key="customData.powerSource">
            <ListItemText
              primary={
                catalogProduct?.customData?.powerSource != null
                  ? t(`generator:${catalogProduct.customData.powerSource}`)
                  : '-'
              }
              secondary={t('generator:powerSource')}
            />
          </ListItemButton>
        )}

      {isElectricalCabinetOrSpineModuleCatalogProduct(catalogProduct) &&
        catalogProduct?.customData?.fuse != null && (
          <ListItemButton key="customData.fuse">
            <ListItemText
              primary={
                catalogProduct?.customData?.fuse != null
                  ? `${catalogProduct.customData.fuse} A`
                  : '-'
              }
              secondary={t('nominalCurrent')}
            />
          </ListItemButton>
        )}

      {isElectricalCabinetOrSpineModuleCatalogProduct(catalogProduct) &&
        catalogProduct?.customData?.inputCables != null && (
          <ListItemButton key="customData.inputCables">
            <ListItemText
              primary={
                catalogProduct?.customData?.inputCables != null
                  ? catalogProduct.customData.inputCables
                  : '-'
              }
              secondary={t('inputCables')}
            />
          </ListItemButton>
        )}

      {isElectricalCabinetOrSpineModuleCatalogProduct(catalogProduct) &&
        catalogProduct?.customData?.isMainDistributionAssembly != null && (
          <ListItemButton key="customData.isMainDistributionAssembly">
            <ListItemText
              primary={
                catalogProduct?.customData?.isMainDistributionAssembly != null
                  ? catalogProduct.customData.isMainDistributionAssembly
                    ? t('actions:yes')
                    : t('actions:nope')
                  : '-'
              }
              secondary={t('mainDistributionAssembly')}
            />
          </ListItemButton>
        )}

      {isHeaterCatalogProduct(catalogProduct) && catalogProduct.customData.heatingPower != null && (
        <ListItemButton key="customData.heatingPower">
          <ListItemText
            primary={
              catalogProduct?.customData?.heatingPower != null
                ? `${catalogProduct.customData.heatingPower} kW`
                : '-'
            }
            secondary={t('heater:heatingPower')}
          />
        </ListItemButton>
      )}

      {isAuroraCatalogProduct(catalogProduct) && catalogProduct.customData.heatingPower != null && (
        <ListItemButton key="customData.heatingPower">
          <ListItemText
            primary={
              catalogProduct?.customData?.heatingPower != null
                ? `${catalogProduct.customData.heatingPower} kW`
                : '-'
            }
            secondary={t('aurora:heatingPower')}
          />
        </ListItemButton>
      )}

      {isValveCatalogProduct(catalogProduct) && showCustomData && (
        <ValveCustomDataListItems catalogProduct={catalogProduct} />
      )}

      {catalogProduct.description !== null && catalogProduct.description !== '' && (
        <ListItemButton key="description">
          <ListItemText primary={catalogProduct.description} secondary={t('description')} />
        </ListItemButton>
      )}

      {isSpineMultiMeasurementCatalogProduct(catalogProduct) && (
        <>
          <ListItemButton key="outputs">
            <ListItemText primary={formatOutputs(catalogProduct)} secondary={t('outputs')} />
          </ListItemButton>

          {catalogProduct.customData?.shipAsInstallationKit != null && (
            <ListItemButton key="shipAsInstallationKit">
              <ListItemText
                primary={
                  catalogProduct.customData?.shipAsInstallationKit
                    ? t('actions:yes')
                    : t('actions:nope')
                }
                secondary={t('shipAsInstallationKit')}
              />
            </ListItemButton>
          )}
        </>
      )}

      {catalogProductPhotoUrl && (
        <ListItemButton key="photo" sx={{ display: 'block', alignItems: 'flex-start' }}>
          <ListItemText secondary={t('common:photo')} />
          <Box
            sx={{
              display: 'inline-block',
              overflow: 'hidden',
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              mt: 1
            }}
          >
            <ImageWithPreview
              src={catalogProductPhotoUrl}
              alt={catalogProduct.name}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '280px',
                objectFit: 'contain'
              }}
            />
          </Box>
        </ListItemButton>
      )}
    </List>
  )
}

interface ValveCustomDataListItemsProps {
  catalogProduct: ValveCatalogProduct
}

function ValveCustomDataListItems({ catalogProduct }: ValveCustomDataListItemsProps) {
  const { t } = useTranslation('catalogProduct')

  const { spine_config, electricalCabinet, heater, inRuuviTag, outRuuviTag } =
    catalogProduct.customData ?? {}

  const electricalCabinetCatalogProduct = useLinkedCatalogProduct(electricalCabinet)

  const heaterCatalogProduct = useLinkedCatalogProduct(heater)

  const inRuuviTagCatalogProduct = useLinkedCatalogProduct(inRuuviTag)

  const outRuuviTagCatalogProduct = useLinkedCatalogProduct(outRuuviTag)

  /**
   * The electrical cabinet values come from the linked electrical cabinet catalog product.
   */
  const electricalCabinetCustomData =
    electricalCabinetCatalogProduct &&
    isElectricalCabinetOrSpineModuleCatalogProduct(electricalCabinetCatalogProduct)
      ? electricalCabinetCatalogProduct.customData
      : undefined

  /**
   * The heater values come from the linked heater catalog product.
   */
  const heaterCustomData =
    heaterCatalogProduct && isHeaterCatalogProduct(heaterCatalogProduct)
      ? heaterCatalogProduct.customData
      : undefined

  /**
   * The keys of the expanded sections.
   */
  const [expanded, setExpanded] = useState<string[]>([])

  /**
   * Toggle the section with the given key.
   */
  function toggleExpanded(key: string) {
    setExpanded((keys) =>
      keys.includes(key) ? keys.filter((value) => value !== key) : [...keys, key]
    )
  }

  return (
    <>
      {spine_config && (
        <>
          <ListSubheader>{t('powerSupply')}</ListSubheader>
          {spine_config.max_current != null && (
            <ListItemButton key="spine_config.max_current" sx={{ ml: 2 }}>
              <ListItemText
                primary={`${spine_config.max_current} A`}
                secondary={t('nominalCurrent')}
              />
            </ListItemButton>
          )}
          {spine_config.clamp_ratio != null && (
            <ListItemButton key="spine_config.clamp_ratio" sx={{ ml: 2 }}>
              <ListItemText
                primary={`${spine_config.clamp_ratio}:1`}
                secondary={t('common:clampRatio')}
              />
            </ListItemButton>
          )}
          {spine_config.input_cables != null && (
            <ListItemButton key="spine_config.input_cables" sx={{ ml: 2 }}>
              <ListItemText primary={spine_config.input_cables} secondary={t('inputCables')} />
            </ListItemButton>
          )}
          {spine_config.single_phase != null && (
            <ListItemButton key="spine_config.single_phase" sx={{ ml: 2 }}>
              <ListItemText
                primary={
                  spine_config.single_phase === 1 ? t('common:singlePhase') : t('common:threePhase')
                }
                secondary={t('common:phase')}
              />
            </ListItemButton>
          )}
        </>
      )}

      {electricalCabinet?.catalogProductId && (
        <>
          <ListSubheader
            onClick={() => toggleExpanded('electricalCabinet')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            {t('electricalCabinet')}
            {expanded.includes('electricalCabinet') ? <ExpandLess /> : <ExpandMore />}
          </ListSubheader>
          <Collapse in={expanded.includes('electricalCabinet')}>
            <LinkedCatalogProductListItem
              label={t('catalogProduct')}
              sx={{ ml: 2 }}
              link={{ ...electricalCabinet, catalogProduct: electricalCabinetCatalogProduct }}
            />
            {electricalCabinetCatalogProduct?.manufacturer && (
              <CatalogProductManufacturerListItem
                catalogProduct={electricalCabinetCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
            {electricalCabinetCustomData?.fuse != null && (
              <ListItemButton key="electricalCabinet.fuse" sx={{ ml: 2 }}>
                <ListItemText
                  primary={`${electricalCabinetCustomData.fuse} A`}
                  secondary={t('nominalCurrent')}
                />
              </ListItemButton>
            )}
            {electricalCabinetCustomData?.inputCables != null && (
              <ListItemButton key="electricalCabinet.inputCables" sx={{ ml: 2 }}>
                <ListItemText
                  primary={electricalCabinetCustomData.inputCables}
                  secondary={t('inputCables')}
                />
              </ListItemButton>
            )}
            {electricalCabinetCustomData?.isMainDistributionAssembly != null && (
              <ListItemButton key="electricalCabinet.isMainDistributionAssembly" sx={{ ml: 2 }}>
                <ListItemText
                  primary={
                    electricalCabinetCustomData.isMainDistributionAssembly
                      ? t('actions:yes')
                      : t('actions:nope')
                  }
                  secondary={t('mainDistributionAssembly')}
                />
              </ListItemButton>
            )}
          </Collapse>
        </>
      )}

      {heater?.catalogProductId && (
        <>
          <ListSubheader
            onClick={() => toggleExpanded('heater')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            {t('heater')}
            {expanded.includes('heater') ? <ExpandLess /> : <ExpandMore />}
          </ListSubheader>
          <Collapse in={expanded.includes('heater')}>
            <LinkedCatalogProductListItem
              label={t('catalogProduct')}
              sx={{ ml: 2 }}
              link={{ ...heater, catalogProduct: heaterCatalogProduct }}
            />
            {heaterCustomData?.type != null && (
              <ListItemButton key="heater.type" sx={{ ml: 2 }}>
                <ListItemText
                  primary={t(`heaterTypes.${heaterCustomData.type}`)}
                  secondary={t('heaterType')}
                />
              </ListItemButton>
            )}
            {heaterCustomData?.heatingSource != null && (
              <ListItemButton key="heater.heatingSource" sx={{ ml: 2 }}>
                <ListItemText
                  primary={t(`heatingSources.${heaterCustomData.heatingSource}`)}
                  secondary={t('heatingSource')}
                />
              </ListItemButton>
            )}
            {heaterCustomData?.heatingPower != null && (
              <ListItemButton key="heater.heatingPower" sx={{ ml: 2 }}>
                <ListItemText
                  primary={`${heaterCustomData.heatingPower} kW`}
                  secondary={t('heater:heatingPower')}
                />
              </ListItemButton>
            )}
          </Collapse>
        </>
      )}

      {inRuuviTag?.catalogProductId && (
        <>
          <ListSubheader
            onClick={() => toggleExpanded('inRuuviTag')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            {t('inRuuviTag')}
            {expanded.includes('inRuuviTag') ? <ExpandLess /> : <ExpandMore />}
          </ListSubheader>
          <Collapse in={expanded.includes('inRuuviTag')}>
            <LinkedCatalogProductListItem
              label={t('catalogProduct')}
              link={{ ...inRuuviTag, catalogProduct: inRuuviTagCatalogProduct }}
              sx={{ ml: 2 }}
            />
            {inRuuviTagCatalogProduct?.manufacturer && (
              <CatalogProductManufacturerListItem
                catalogProduct={inRuuviTagCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
            {inRuuviTagCatalogProduct?.manufacturerProductCode && (
              <CatalogProductManufacturerProductCodeListItem
                catalogProduct={inRuuviTagCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
          </Collapse>
        </>
      )}

      {outRuuviTag?.catalogProductId && (
        <>
          <ListSubheader
            onClick={() => toggleExpanded('outRuuviTag')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            {t('outRuuviTag')}
            {expanded.includes('outRuuviTag') ? <ExpandLess /> : <ExpandMore />}
          </ListSubheader>
          <Collapse in={expanded.includes('outRuuviTag')}>
            <LinkedCatalogProductListItem
              label={t('catalogProduct')}
              link={{ ...outRuuviTag, catalogProduct: outRuuviTagCatalogProduct }}
              sx={{ ml: 2 }}
            />
            {outRuuviTagCatalogProduct?.manufacturer && (
              <CatalogProductManufacturerListItem
                catalogProduct={outRuuviTagCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
            {outRuuviTagCatalogProduct?.manufacturerProductCode && (
              <CatalogProductManufacturerProductCodeListItem
                catalogProduct={outRuuviTagCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
          </Collapse>
        </>
      )}
    </>
  )
}

interface LinkedCatalogProductListItemProps extends Omit<ListItemButtonProps, 'children'> {
  label: string
  link: CatalogProductLink
}

/**
 * Shows the name of a linked catalog product.
 */
function LinkedCatalogProductListItem({
  label,
  link,
  ...props
}: LinkedCatalogProductListItemProps) {
  const linkedCatalogProduct = useLinkedCatalogProduct(link)

  return (
    <ListItemButton {...props}>
      <ListItemText primary={linkedCatalogProduct?.name ?? '-'} secondary={label} />
    </ListItemButton>
  )
}

export interface CatalogProductDetailsListItemProps extends Omit<ListItemButtonProps, 'children'> {
  catalogProduct: CatalogProduct
}

export function CatalogProductNameListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.name ?? t('noName')}
        primaryTypographyProps={{
          color: catalogProduct.name ? 'inherit' : 'text.disabled'
        }}
        secondary={t('name')}
      />
    </ListItemButton>
  )
}

export function CatalogProductManufacturerListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.manufacturer ?? t('noManufacturer')}
        primaryTypographyProps={{
          color: catalogProduct.manufacturer ? 'inherit' : 'text.disabled'
        }}
        secondary={t('manufacturer')}
      />
    </ListItemButton>
  )
}

export function CatalogProductManufacturerProductCodeListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.manufacturerProductCode ?? t('noManufacturerProductCode')}
        primaryTypographyProps={{
          color: catalogProduct.manufacturerProductCode ? 'inherit' : 'text.disabled'
        }}
        secondary={t('manufacturerProductCode')}
      />
    </ListItemButton>
  )
}
